/**
 * ACC-C · El CATÁLOGO: qué cosméticos existen, en qué hueco van y qué cuestan.
 *
 * Vivía en `src/lobby/profile.ts`, es decir, **solo en el navegador**. Mientras
 * nada costara dinero eso era simplemente el sitio equivocado; en cuanto el
 * servidor tenga que decidir si puedes ponerte algo, deja de ser un detalle: si
 * la única lista de objetos está en el cliente, el cliente es quien dice qué
 * existe y cuánto vale. Eso es pedirle al comprador que ponga el precio.
 *
 * Aquí, en un paquete sin DOM, lo leen los dos: el navegador para dibujar la
 * ficha y el servicio de identidad para validar. Una sola lista, y el servidor
 * no se cree nada de lo que le manden.
 *
 * ## Huecos, no "iconos"
 *
 * Un cosmético ocupa un HUECO (`Slot`) y solo puede haber uno puesto por hueco.
 * Hoy el único hueco con objetos es `icon`; `trail` está declarado porque la
 * maquinaria (tabla, endpoint, validación) es la misma y no quiero migrar nada
 * el día que haya estelas de colores. Lo que NO hay es un catálogo de estelas
 * anunciado y vacío: un objeto listado que no se puede tener es exactamente el
 * señuelo que este proyecto viene quitando. Cuando existan, se añaden aquí y ya
 * están vendidas y equipables sin tocar ni el esquema ni las rutas.
 *
 * ## Gratis no es lo mismo que comprado
 *
 * Los objetos sin precio NO generan una fila en `entitlements`. Si la generaran,
 * cada cuenta nueva escribiría ocho filas para decir algo que ya sabe el
 * catálogo, y el día que uno gratis pase a ser de pago habría que ir a buscarlas.
 * "Lo tienes" se calcula: `free(item) || consta comprado`. Ver `canEquip`.
 *
 * ## Sobre el precio
 *
 * `priceCents` es lo que se enseña hoy (es lo que ya había). El precio EN
 * MONEDAS llega con la Fase D junto a la pasarela; hasta que exista, no se
 * inventa un número aquí, porque un precio en monedas sin monedas que comprar es
 * una promesa a medias. Ver docs/PLAN-TIENDA.md §1.2.
 */

/** Hueco del equipamiento. Uno puesto por hueco, como mucho. */
export type Slot = "icon" | "trail";

export const SLOTS: readonly Slot[] = ["icon", "trail"];

export interface CatalogItem {
  /**
   * Id corto, único DENTRO de su hueco. Es lo que viaja por la red en
   * `PeerInfo.icon`, y por eso se conserva tal cual: cambiarlo por el sku
   * completo rompería a cualquier cliente o edge que aún no se haya actualizado.
   */
  id: string;
  slot: Slot;
  /** Clave global (`icon:bolt`). Es lo que se guarda en `entitlements`. */
  sku: string;
  /** Lo que se dibuja. Para iconos, un emoji: cero descargas, cero atlas. */
  glyph: string;
  name: string;
  /** Precio en céntimos de euro. `0` = incluido con la cuenta, sin comprar nada. */
  priceCents: number;
}

/** Construye la entrada del catálogo derivando el sku, para que no se escriba a mano. */
function item(slot: Slot, id: string, glyph: string, name: string, priceCents: number): CatalogItem {
  return { id, slot, sku: `${slot}:${id}`, glyph, name, priceCents };
}

/**
 * El valor de "ningún icono" en el hueco `icon`.
 *
 * No es un objeto: es la ausencia de uno. Está en el catálogo para que quitarse
 * el icono use el mismo camino que ponérselo (no hay un `DELETE` aparte), y es
 * gratis para todo el mundo, con cuenta y sin ella.
 */
export const NONE = "none";

/**
 * Catálogo. Los gratis son deliberadamente suficientes para jugar y
 * distinguirse: la personalización básica no se cobra, se cobra la vanidad.
 */
export const CATALOG: readonly CatalogItem[] = [
  item("icon", NONE, "", "Sin icono", 0),
  item("icon", "bolt", "⚡", "Rayo", 0),
  item("icon", "fire", "🔥", "Fuego", 0),
  item("icon", "star", "★", "Estrella", 0),
  item("icon", "skull", "💀", "Calavera", 0),
  item("icon", "heart", "❤", "Corazón", 0),
  item("icon", "eye", "👁", "Ojo", 0),
  item("icon", "wave", "🌊", "Ola", 0),
  item("icon", "crown", "👑", "Corona", 199),
  item("icon", "dragon", "🐉", "Dragón", 299),
  item("icon", "alien", "👽", "Alien", 199),
  item("icon", "ghost", "👻", "Fantasma", 199),
  item("icon", "rocket", "🚀", "Cohete", 299),
  item("icon", "diamond", "💎", "Diamante", 399),
];

/** Los objetos de un hueco, en el orden del catálogo. */
export function itemsOf(slot: Slot): CatalogItem[] {
  return CATALOG.filter((i) => i.slot === slot);
}

/** Busca por hueco + id. `undefined` si esta versión no lo conoce. */
export function findItem(slot: Slot, id: string | undefined): CatalogItem | undefined {
  if (!id) return undefined;
  return CATALOG.find((i) => i.slot === slot && i.id === id);
}

/** Busca por sku (`icon:bolt`). */
export function findSku(sku: string | undefined): CatalogItem | undefined {
  if (!sku) return undefined;
  return CATALOG.find((i) => i.sku === sku);
}

/** ¿Va incluido con la cuenta, sin comprar nada? */
export function isFree(i: CatalogItem): boolean {
  return i.priceCents === 0;
}

/**
 * ¿Puede esta cuenta ponerse este objeto?
 *
 * Es LA regla del inventario, y por eso está aquí y no repartida entre la ruta
 * y la interfaz: el navegador la usa para pintar el candado y el servicio para
 * rechazar la petición, y ninguna de las dos versiones puede irse por su lado.
 *
 * `owned` son los skus que constan comprados. Un objeto que el catálogo no
 * conoce se rechaza aunque figure comprado: puede ser de una versión posterior,
 * o basura, y ponerse algo que no existe deja un hueco vacío en la cancha.
 */
export function canEquip(slot: Slot, id: string, owned: readonly string[]): boolean {
  const it = findItem(slot, id);
  if (!it) return false;
  return isFree(it) || owned.includes(it.sku);
}

/** Precio legible ("Gratis" / "1,99 €"). */
export function priceLabel(i: CatalogItem): string {
  if (isFree(i)) return "Gratis";
  return `${(i.priceCents / 100).toFixed(2).replace(".", ",")} €`;
}

/**
 * Lo que lleva puesto una cuenta: hueco → id del objeto.
 *
 * Un mapa parcial a propósito: un hueco sin entrada es "nada puesto", que es
 * distinto de tener que escribir `none` en todos los huecos que existan hoy.
 */
export type Loadout = Partial<Record<Slot, string>>;

/**
 * Limpia un equipamiento contra el catálogo y lo comprado.
 *
 * Se aplica AL LEER, no solo al escribir. Lo guardado no manda sobre lo que el
 * catálogo dice hoy: un objeto retirado, o uno que dejó de ser gratis, o uno de
 * un reembolso, tiene que caer solo. Si esto solo se comprobara al equipar, un
 * objeto retirado seguiría puesto para siempre en quien ya lo llevaba.
 */
export function sanitizeLoadout(l: Loadout, owned: readonly string[]): Loadout {
  const out: Loadout = {};
  for (const slot of SLOTS) {
    const id = l[slot];
    if (id && canEquip(slot, id, owned)) out[slot] = id;
  }
  return out;
}
